import React from "react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Layout from "../Layout"
import CareerSecond from "../CareerSecond"
import { HERO, WORD_BAND, WHY_JOIN, ROLES_SECTION, NEXT_STEPS, FAQ, LIFE_BANNER, CLOSING } from "../../data/career"

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

// The exact icon set src/components/CareerFirst.js already used for these
// four values (not rebuilt here — CareerFirst.js's own use on this page is
// retired, but its icon shapes are reused as asked). Sized at 36px to match
// the spec's icon-holder treatment (src/styles/careerPage.scss's
// &__why-icon, t.$s-8/64px square, t.$r-icon/20px radius) — kept in sync by
// hand, the same as every other build-time numeric value in this codebase
// that has to match a CSS token.
const ICON_SIZE = 36

const IconBook = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
)

const IconBalance = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="3" x2="12" y2="21" />
    <path d="M5 8l7-5 7 5" />
    <path d="M5 8c0 2.761 2.239 5 5 5s5-2.239 5-5" />
    <path d="M9 8c0 2.761 2.239 5 5 5s5-2.239 5-5" />
    <line x1="3" y1="21" x2="21" y2="21" />
  </svg>
)

const IconGift = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <path d="M12 22V7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
)

const IconHeart = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const ICONS = { book: IconBook, balance: IconBalance, gift: IconGift, heart: IconHeart }

/**
 * /career — redesign. Every section's text comes from src/data/career.js;
 * this component only arranges it. The open-roles list (Section 4) is
 * <CareerSecond>, unchanged data-wise — `careerData` is passed straight
 * through from src/pages/career.js's own fetch, exactly as it was before
 * this redesign.
 *
 * `heroDeskPhoto`/`heroPhoto` come from src/pages/career.js's page query, or
 * null if not found (see src/data/career.js's HERO comment for which photos
 * those are).
 */
const CareerPage = ({ heroDeskPhoto, heroPhoto, careerData }) => (
  <Layout
    pageTitle="Visiomate - Career"
    descrip="Join a team that values innovation, collaboration, and work-life balance. Discover fulfilling career opportunities at Visiomate. Apply now!"
  >
    <div className="car">
      {/* 1. HERO */}
      <section className="car__hero">
        <span className="car__hero-ring" aria-hidden="true" />
        <div className="car__hero-inner">
          <div className="car__hero-text">
            <p className="car__hero-eyebrow">{HERO.eyebrow}</p>
            <h1 className="car__hero-heading">{HERO.heading}</h1>
            <p className="car__hero-body">{HERO.body}</p>
            <div className="car__hero-ctas">
              <a className="car__hero-cta--primary" href={HERO.primaryCta.href}>
                {HERO.primaryCta.label}
              </a>
              <Link className="car__hero-cta--secondary" to={HERO.secondaryCta.to}>
                {HERO.secondaryCta.label}
              </Link>
            </div>
          </div>
          <div className="car__hero-photos">
            <div className="car__hero-photo car__hero-photo--desk">
              {heroDeskPhoto && (
                <GatsbyImage image={heroDeskPhoto} alt={HERO.photoDeskAlt} className="car__hero-photo-img" loading="eager" />
              )}
            </div>
            <div className="car__hero-photo car__hero-photo--shirt">
              {heroPhoto && (
                <GatsbyImage image={heroPhoto} alt={HERO.photoAlt} className="car__hero-photo-img" loading="eager" />
              )}
            </div>
            <a className="car__hero-tile" href={HERO.primaryCta.href}>
              <span className="car__hero-tile-heading">{HERO.tileHeading}</span>
              <span className="car__hero-tile-sub">
                {HERO.tileSub}
                <ArrowIcon />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* 2. WORD BAND */}
      <section className="car__band">
        <div className="car__band-inner">
          {WORD_BAND.map((word, i) => (
            <React.Fragment key={word}>
              {i > 0 && <span className="car__band-star" aria-hidden="true">★</span>}
              <span className="car__band-word">{word}</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* 3. WHY JOIN US */}
      <section className="car__why">
        <div className="car__why-inner">
          <p className="car__why-eyebrow">{WHY_JOIN.eyebrow}</p>
          <h2 className="car__why-heading">{WHY_JOIN.heading}</h2>
          <div className="car__why-grid">
            {WHY_JOIN.tiles.map(tile => {
              const Icon = ICONS[tile.icon]
              return (
                <div
                  key={tile.key}
                  className={`car__why-tile car__why-tile--${tile.theme}`}
                  style={{ gridColumn: `span ${tile.span}` }}
                >
                  <span className="car__why-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <p className="car__why-label">{tile.label}</p>
                  <p className="car__why-text">{tile.text}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* 4. OPEN ROLES */}
      <section className="car__roles" id="roles">
        <div className="car__roles-inner">
          <div className="car__roles-header">
            <p className="car__roles-eyebrow">{ROLES_SECTION.eyebrow}</p>
            <h2 className="car__roles-heading">{ROLES_SECTION.heading}</h2>
            <p className="car__roles-intro">{ROLES_SECTION.intro}</p>
          </div>

          <CareerSecond careerData={careerData} />

          <div className="car__cv-card">
            <div>
              <h3 className="car__cv-title">{ROLES_SECTION.cvCard.title}</h3>
              <p className="car__cv-text">{ROLES_SECTION.cvCard.body}</p>
            </div>
            <a className="car__cv-cta" href={ROLES_SECTION.cvCard.cta.href}>
              {ROLES_SECTION.cvCard.cta.label}
            </a>
          </div>
        </div>
      </section>

      {/* 5. WHAT HAPPENS NEXT */}
      <section className="car__steps">
        <div className="car__steps-inner">
          <p className="car__steps-eyebrow">{NEXT_STEPS.eyebrow}</p>
          <h2 className="car__steps-heading">{NEXT_STEPS.heading}</h2>
          <div className="car__steps-row">
            {NEXT_STEPS.steps.map(step => (
              <div className="car__step" key={step.num}>
                <div className="car__step-row">
                  <span className={`car__step-marker car__step-marker--${step.theme}`}>{step.num}</span>
                  <span className="car__step-line" aria-hidden="true" />
                </div>
                <h3 className="car__step-title">{step.title}</h3>
                <p className="car__step-text">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. QUESTIONS */}
      <section className="car__faq" id="faq">
        <div className="car__faq-inner">
          <p className="car__faq-eyebrow">{FAQ.eyebrow}</p>
          <h2 className="car__faq-heading">{FAQ.heading}</h2>
          <div className="car__faq-grid">
            {FAQ.items.map(item => (
              <div className={`car__faq-card car__faq-card--${item.theme}`} key={item.q}>
                <span className="car__faq-marker" aria-hidden="true">?</span>
                <div>
                  <h3 className="car__faq-q">{item.q}</h3>
                  <p className="car__faq-a">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. LIFE BANNER */}
      <section className="car__life-banner">
        <div className="car__life-banner-inner">
          <Link className="car__life-banner-link" to={LIFE_BANNER.to}>
            <span className="car__life-banner-left">{LIFE_BANNER.left}</span>
            <span className="car__life-banner-right">
              {LIFE_BANNER.right}
              <ArrowIcon />
            </span>
          </Link>
        </div>
      </section>

      {/* 8. CLOSING BAND */}
      <section className="car__closing">
        <div className="car__closing-inner">
          <div>
            <h2 className="car__closing-heading">{CLOSING.heading}</h2>
            <p className="car__closing-body">{CLOSING.body}</p>
          </div>
          <a className="car__closing-cta" href={CLOSING.cta.href}>
            {CLOSING.cta.label}
          </a>
        </div>
      </section>
    </div>
  </Layout>
)

export default CareerPage
